/**
 * Mobile detection for attendance and app access.
 *
 * Uses OS / platform signals only (Client Hints, user agent, navigator.platform).
 * Viewport width and generic touch support are intentionally not used — they
 * misclassify touch laptops and miss mobile "desktop site" mode.
 */

type NavigatorWithUAData = Navigator & {
  userAgentData?: {
    mobile?: boolean;
    platform?: string;
  };
};

const MOBILE_UA_REGEX =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

const MOBILE_PLATFORM_REGEX = /iPhone|iPad|iPod|Android/i;

const MOBILE_OS_PLATFORM_REGEX = /android|ios/i;

const DESKTOP_UA_REGEX = /Windows NT|Macintosh|X11|CrOS/i;

function getUserAgent(): string {
  return (
    navigator.userAgent ||
    navigator.vendor ||
    (window as Window & { opera?: string }).opera ||
    ""
  );
}

function getClientHints(): { mobile?: boolean; platform?: string } | null {
  const uaData = (navigator as NavigatorWithUAData).userAgentData;
  if (!uaData) return null;
  return {
    mobile: uaData.mobile,
    platform: uaData.platform,
  };
}

function isMobileOsPlatform(platform: string): boolean {
  return MOBILE_OS_PLATFORM_REGEX.test(platform);
}

/**
 * True when the device OS is a phone/tablet (Android, iOS, iPadOS, etc.),
 * including when the browser is in "desktop site" / desktop UA mode.
 */
export function isMobileOS(): boolean {
  const hints = getClientHints();
  const ua = getUserAgent();
  const legacyPlatform = navigator.platform || "";

  if (hints?.platform && isMobileOsPlatform(hints.platform)) {
    return true;
  }

  if (hints?.mobile === true) {
    return true;
  }

  if (MOBILE_UA_REGEX.test(ua)) {
    return true;
  }

  if (MOBILE_PLATFORM_REGEX.test(legacyPlatform)) {
    return true;
  }

  return false;
}

/**
 * True when the user is likely on a mobile device using desktop-site / spoofed
 * desktop user agent (e.g. iPhone Safari → "Macintosh", Android → wide desktop UA).
 */
export function isDesktopSiteOnMobile(): boolean {
  if (!isMobileOS()) {
    return false;
  }

  const hints = getClientHints();
  const ua = getUserAgent();
  const legacyPlatform = navigator.platform || "";

  if (hints?.platform && isMobileOsPlatform(hints.platform) && hints.mobile === false) {
    return true;
  }

  const onMobileLegacyPlatform = MOBILE_PLATFORM_REGEX.test(legacyPlatform);
  const desktopUa = DESKTOP_UA_REGEX.test(ua);
  const noMobileTokenInUa = !MOBILE_UA_REGEX.test(ua);

  if (onMobileLegacyPlatform && desktopUa && noMobileTokenInUa) {
    return true;
  }

  return false;
}

/** @deprecated Prefer isMobileOS — kept for existing imports. */
export const isMobileDevice = isMobileOS;

export interface MobileEnvironmentInfo {
  /** Device runs a mobile OS (phone/tablet), not merely a narrow viewport. */
  isMobile: boolean;
  isDesktopSiteOnMobile: boolean;
  userAgent: string;
  /** Client Hints platform when available (e.g. "Android", "Windows"). */
  platform?: string;
}

export const getMobileEnvironmentInfo = (): MobileEnvironmentInfo => {
  const userAgent = getUserAgent();
  const hints = getClientHints();
  const isMobile = isMobileOS();

  return {
    isMobile,
    isDesktopSiteOnMobile: isDesktopSiteOnMobile(),
    userAgent,
    platform: hints?.platform,
  };
};
